/**
 * AccountRequestDetailPage - View details of an account request
 *
 * Route: /admin/account-requests/:id
 */

import { useState, useCallback } from 'react'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { Badge } from '@/common/components/badge'
import { AlertDialog } from '@/common/components/alert-dialog'
import { Input } from '@/common/components/input'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import {
  getAccountRequest,
  approveAccountRequest,
  rejectAccountRequest,
  downloadPofDocument,
} from '@/features/account-request/api/adminAccountRequests'
import * as styles from './$id.css'

export const Route = createFileRoute({
  component: AccountRequestDetailPage,
})

// Display label maps for qualification fields
const YES_NO_SOMETIMES_LABELS: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  sometimes: 'Sometimes',
}

const PURCHASE_VOLUME_LABELS: Record<string, string> = {
  less_than_5: 'Less than 5',
  '5_to_10': '5-10',
  '10_to_25': '10-25',
  '25_to_50': '25-50',
  more_than_50: 'More than 50 on an annual basis',
}

const REFERRAL_SOURCE_LABELS: Record<string, string> = {
  online_search: 'Online Search',
  agent_referral: 'Agent Referral',
  investor_referral: 'Investor Referral',
  other: 'Other',
}

// Map status to Badge color
const STATUS_BADGE_COLOR: Record<string, 'caution' | 'success' | 'error' | 'neutral'> = {
  pending: 'caution',
  approved: 'success',
  rejected: 'error',
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatPhone(phone: string | null): string {
  if (!phone) return '-'
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '')
  // Format as (xxx) xxx-xxxx if 10 digits
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  // Return original if not 10 digits
  return phone
}

// =============================================================================
// Field Component
// =============================================================================

interface FieldProps {
  label: string
  children: React.ReactNode
  fullWidth?: boolean
}

function Field({ label, children, fullWidth }: FieldProps) {
  return (
    <div className={`${styles.field} ${fullWidth ? styles.fieldFullWidth : ''}`}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.fieldValue}>{children}</div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

function AccountRequestDetailPage() {
  const { id } = useParams({ strict: false })
  const navigate = useNavigate()
  const { token, roles } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const queryClient = useQueryClient()

  // Fetch request data
  const {
    data: request,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-account-request', id],
    queryFn: () => getAccountRequest(token!, id!),
    enabled: !!token && !!id,
  })

  // Approve confirmation state
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [isApproving, setIsApproving] = useState(false)

  // Reject confirmation state
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [isRejecting, setIsRejecting] = useState(false)

  // POF download state
  const [isDownloadingPof, setIsDownloadingPof] = useState(false)

  // Check if current user is admin
  const isAdmin = roles.includes('admin')

  // Handle cancel - go back to list
  const handleCancel = useCallback(() => {
    navigate({ to: '/admin/account-requests' })
  }, [navigate])

  // Handle approve
  const handleApprove = useCallback(async () => {
    if (!token || !id) return

    setIsApproving(true)
    try {
      const result = await approveAccountRequest(token, id)
      showSuccessToast(result.message)
      setShowApproveDialog(false)
      await queryClient.invalidateQueries({ queryKey: ['admin-account-request', id] })
      await queryClient.invalidateQueries({ queryKey: ['dataSource', 'admin-account-requests'] })
    } catch (err) {
      showErrorToast(getErrorMessage(err) || 'Failed to approve account request')
    } finally {
      setIsApproving(false)
    }
  }, [token, id, showSuccessToast, showErrorToast, queryClient])

  // Handle reject
  const handleReject = useCallback(async () => {
    if (!token || !id) return

    setIsRejecting(true)
    try {
      await rejectAccountRequest(token, id, rejectReason || undefined)
      showSuccessToast('Account request rejected.')
      setShowRejectDialog(false)
      setRejectReason('')
      await queryClient.invalidateQueries({ queryKey: ['admin-account-request', id] })
      await queryClient.invalidateQueries({ queryKey: ['dataSource', 'admin-account-requests'] })
    } catch (err) {
      showErrorToast(getErrorMessage(err) || 'Failed to reject account request')
    } finally {
      setIsRejecting(false)
    }
  }, [token, id, rejectReason, showSuccessToast, showErrorToast, queryClient])

  // Handle POF download
  const handleDownloadPof = useCallback(async () => {
    if (!token || !id) return

    setIsDownloadingPof(true)
    try {
      await downloadPofDocument(token, id)
    } catch (err) {
      showErrorToast(getErrorMessage(err) || 'Failed to download document')
    } finally {
      setIsDownloadingPof(false)
    }
  }, [token, id, showErrorToast])

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error or not found state
  if (error || !request) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Icon name="error_outline" size={48} color="muted" />
          <Text as="h2" variant="bodyLg" weight="bold">
            Request Not Found
          </Text>
          <Text variant="body" color="muted" style={{ maxWidth: 400 }}>
            The account request you're looking for doesn't exist or you don't have permission to view
            it.
          </Text>
          <Link to="/admin/account-requests">
            <Button>Back to Account Requests</Button>
          </Link>
        </div>
      </div>
    )
  }

  // Non-admin unauthorized view
  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Icon name="lock" size={48} color="muted" />
          <Text as="h2" variant="bodyLg" weight="bold">
            Access Restricted
          </Text>
          <Text variant="body" color="muted" style={{ maxWidth: 400 }}>
            Account request management is only available to administrators.
          </Text>
        </div>
      </div>
    )
  }

  const isPending = request.status === 'pending'

  return (
    <>
      <div className={styles.container}>
        {/* Back link */}
        <Link to="/admin/account-requests" className={styles.backLink}>
          <Icon name="arrow_back" size={20} />
          Back to Account Requests
        </Link>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <Text as="h1" variant="h4" weight="bold">
              {request.requested_role === 'agent' ? 'Agent Account Request' : 'Investor Account Request'}
            </Text>
            <Badge
              color={STATUS_BADGE_COLOR[request.status] ?? 'neutral'}
              style={{ textTransform: 'capitalize' }}
            >
              {request.status}
            </Badge>
          </div>
          <Text variant="bodySm" color="muted">
            Submitted {formatDateTime(request.created_at)}
          </Text>
        </div>

        {/* Contact Information Card */}
        <div className={styles.card}>
          <Text as="h2" variant="bodyLg" weight="bold" className={styles.cardHeader}>
            Contact Information
          </Text>
          <div className={styles.fieldGrid}>
            <Field label="Email">{request.email}</Field>
            <Field label="Phone">{formatPhone(request.phone)}</Field>
            <Field label="First Name">{request.first_name}</Field>
            <Field label="Last Name">{request.last_name}</Field>
          </div>
        </div>

        {/* Company Information Card */}
        <div className={styles.card}>
          <Text as="h2" variant="bodyLg" weight="bold" className={styles.cardHeader}>
            {request.requested_role === 'agent' ? 'Brokerage Information' : 'Company Information'}
          </Text>
          <div className={styles.fieldGrid}>
            <Field label={request.requested_role === 'agent' ? 'Brokerage' : 'Company Name'}>
              {request.company_name || '-'}
            </Field>
            <Field label="Markets" fullWidth>
              {request.markets.length > 0 ? (
                <div className={styles.marketBadges}>
                  {request.markets.map((market, index) => (
                    <Badge key={`${market}-${index}`} color="neutral">
                      {market}
                    </Badge>
                  ))}
                </div>
              ) : (
                '-'
              )}
            </Field>
          </div>
        </div>

        {/* Agent Qualification Card (agents only) */}
        {request.requested_role === 'agent' && request.qualification_data && (
          <div className={styles.card}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.cardHeader}>
              Agent Qualifications
            </Text>
            <div className={styles.fieldGrid}>
              <Field label="License Number">
                {request.qualification_data.license_number || '-'}
              </Field>
              <Field label="License State">
                {request.qualification_data.license_state || '-'}
              </Field>
              <Field label="MLS Boards" fullWidth>
                {request.qualification_data.mls_boards && request.qualification_data.mls_boards.length > 0 ? (
                  <div className={styles.marketBadges}>
                    {request.qualification_data.mls_boards.map((board, index) => (
                      <Badge key={`${board}-${index}`} color="neutral">
                        {board}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  '-'
                )}
              </Field>
            </div>
          </div>
        )}

        {/* Qualification Information Card (only if any qualification data exists) */}
        {(request.wholesale_intent || request.proof_of_funds_available || request.hard_money_lender || request.annual_purchase_volume || request.referral_source) && (
          <div className={styles.card}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.cardHeader}>
              Qualification Information
            </Text>
            <div className={styles.fieldGrid}>
              {request.wholesale_intent && (
                <Field label="Wholesale Intent">
                  {YES_NO_SOMETIMES_LABELS[request.wholesale_intent] ?? request.wholesale_intent}
                </Field>
              )}
              {request.proof_of_funds_available && (
                <Field label="Proof of Funds">
                  {YES_NO_SOMETIMES_LABELS[request.proof_of_funds_available] ?? request.proof_of_funds_available}
                </Field>
              )}
              {request.proof_of_funds_file_name && (
                <Field label="POF Document">
                  <button
                    type="button"
                    className={styles.pofDownloadLink}
                    onClick={handleDownloadPof}
                    disabled={isDownloadingPof}
                  >
                    <Icon name="download" size={16} />
                    {isDownloadingPof ? 'Downloading...' : request.proof_of_funds_file_name}
                  </button>
                </Field>
              )}
              {request.hard_money_lender && (
                <Field label="Hard Money Lender">
                  {YES_NO_SOMETIMES_LABELS[request.hard_money_lender] ?? request.hard_money_lender}
                </Field>
              )}
              {request.hard_money_lender_explanation && (
                <Field label="Hard Money Lender Details">
                  {request.hard_money_lender_explanation}
                </Field>
              )}
              {request.annual_purchase_volume && (
                <Field label="Annual Purchase Volume">
                  {PURCHASE_VOLUME_LABELS[request.annual_purchase_volume] ?? request.annual_purchase_volume}
                </Field>
              )}
              {request.referral_source && (
                <Field label="Referral Source">
                  {REFERRAL_SOURCE_LABELS[request.referral_source] ?? request.referral_source}
                </Field>
              )}
              {request.referral_source_detail && (
                <Field label="Referral Details">
                  {request.referral_source_detail}
                </Field>
              )}
            </div>
          </div>
        )}

        {/* Review Details Card (if not pending) */}
        {!isPending && (
          <div className={styles.card}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.cardHeader}>
              Review Details
            </Text>
            <div className={styles.fieldGrid}>
              <Field label="Reviewed At">{formatDateTime(request.reviewed_at)}</Field>
              <Field label="Reviewed By">{request.reviewed_by_name || '-'}</Field>
              {request.status === 'rejected' && request.rejection_reason && (
                <Field label="Rejection Reason" fullWidth>
                  {request.rejection_reason}
                </Field>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Bar - only show for pending requests */}
      {isPending && (
        <div className={styles.footer}>
          <div className={styles.footerInner}>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <div className={styles.footerSpacer} />
            <Button variant="danger" onClick={() => setShowRejectDialog(true)}>
              Reject
            </Button>
            <Button onClick={() => setShowApproveDialog(true)}>Approve</Button>
          </div>
        </div>
      )}

      {/* Approve confirmation dialog */}
      <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialog.Content>
          <AlertDialog.Title>Approve account request?</AlertDialog.Title>
          <AlertDialog.Description>
            This will create an {request.requested_role} account for "{request.email}" and send them
            a welcome email with instructions to set up their password, unless they already have an
            Orchard login.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isApproving}>Cancel</AlertDialog.Cancel>
            <Button variant="primary" onClick={handleApprove} disabled={isApproving}>
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>

      {/* Reject confirmation dialog */}
      <AlertDialog
        open={showRejectDialog}
        onOpenChange={(open) => {
          setShowRejectDialog(open)
          if (!open) setRejectReason('')
        }}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Reject account request?</AlertDialog.Title>
          <AlertDialog.Description>
            <Stack gap="16">
              <Text>
                This will reject the account request from "{request.email}". They will not be
                notified automatically.
              </Text>
              <Input
                label="Rejection reason (optional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Incomplete information, not a qualified investor"
              />
            </Stack>
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isRejecting}>Cancel</AlertDialog.Cancel>
            <Button variant="danger" onClick={handleReject} disabled={isRejecting}>
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  )
}
